import { entityIdentityKey, renumberBid } from '@repo/front-end-utils';
import type { EntityRef } from '@repo/front-end-utils';
import type { FloorPlannerObserver } from '@repo/frontend-types';

/**
 * What Go Live would file against what the twin is measuring right now — computed BEFORE anything
 * destructive runs, so the operator can be told what stops (`camera-observer-setup-golive-config`,
 * epic ledger row 9).
 *
 * Pure: no I/O, no store reads, no React. Everything it needs is passed in, which is what lets the
 * arithmetic be tested without a twin — the Go Live path itself is destructive and cannot be.
 */

/**
 * PostgREST silently caps an unlimited read at 1,000 rows with an HTTP 200 and no error, and the
 * middleware's observer read is unlimited (`twin-sense-observer.service.ts`, `findAll` with no
 * `limit`). A running set of exactly this length may therefore be a truncated one, and a truncated
 * running set UNDERSTATES what would be lost — which is the one failure that would silently defeat
 * this whole gate. Production's largest configuration holds 241, so it cannot bite today; the
 * caller refuses on it rather than trusting it. See `docs/coding-standards.md`,
 * "Behaviour at the maximum".
 */
export const POSTGREST_MAX_ROWS = 1000;

/**
 * An observer's target entity, with any per-observer ordinal suffix removed.
 *
 * One entity can carry several observers, and the middleware distinguishes them by suffixing the
 * entity identifier: staging configuration 5 holds `NP2-STN-A012.1`, `.2` and `.3` against three
 * different camera feeds, and 19 of its 151 observers are suffixed this way. The plan side carries
 * the bare entity identifier (`entityCode`), so every comparison between the two has to fold
 * through this — matching on the raw value reports every suffixed observer as an entity the plan
 * has never heard of.
 *
 * Deliberately narrow: it strips **one** trailing `.<digits>` group and nothing else. Entity
 * identifiers themselves contain hyphens (`NP2-STN-A012`) but never a dot, so this cannot eat part
 * of a real identifier.
 *
 * Be precise about the two edge shapes, because this module is exported for feature 04 to reuse
 * for the same join and the contract is what a second caller will rely on:
 *
 *  - A **non-numeric** suffix is left alone — `NP2-STN-A012.spare` returns unchanged.
 *  - A **doubled** suffix loses only its last group — `NP2-STN-A012.1.2` returns
 *    `NP2-STN-A012.1`, NOT the argument. So this is not idempotent on that shape, and it is not
 *    safe to fold a value twice. Nothing the middleware produces has two ordinals (it appends
 *    exactly one), which is why the narrow regex is right rather than being tightened to reject
 *    it — but do not read "narrow" as "a no-op on anything unusual".
 *
 * **It composes through `entityIdentityKey`**, the repo's one identity-normalisation rule (R18),
 * rather than comparing raw strings. The ordinal strip is this function's own job — the shared
 * helper does not do it — but the case-and-whitespace fold must be the shared one, because
 * `plan-entity-identity` made that rule load-bearing everywhere an entity identity is compared. A
 * second fold sitting beside it is what drifts. Callers keying a lookup on the result must key it
 * through this same function; `OnboardingPage`'s `namesByBid` does.
 */
export const baseEntityBid = (bid: string): string => entityIdentityKey(bid).replace(/\.\d+$/, '');

/**
 * The least this function needs to know about an observer that is running: which entity it watches,
 * and whether it is actually measuring (a stream and a region).
 *
 * `TwinSenseObserver` satisfies it, and so does a projection of a configuration statement — which
 * is what lets the Go Live pre-flight establish what a twin measures from ONE read instead of two
 * reads of the same rows in two shapes.
 */
export interface RunningObserver {
  /** As stored — suffix and all. This function folds it itself; a pre-folded value folds twice. */
  twin_entity_bid: string;
  /*
   * ⚠ Optional, because `TwinSenseObserver` declares both of these optional and the whole point of
   * this type is that a `TwinSenseObserver` satisfies it. Requiring them compiled fine against the
   * statement projection and broke 29 existing assertions in `observerConfigDiff.test.ts` — which
   * is the check earning its place: "structural, so nothing else moves" is a claim that has to be
   * true rather than asserted.
   *
   * `isMeasuring` tests `!= null`, which is false for `undefined` as well as `null`, so an absent
   * field and a null one mean the same thing here: not measuring.
   */
  rtsp_endpoint_id?: number | null;
  polygon_config?: string | null;
}

const isMeasuring = (o: RunningObserver): boolean =>
  o.rtsp_endpoint_id != null && o.polygon_config != null;

export interface SelectFileableObserversParams {
  /** Observers persisted in the plan (`design.observers`), across every floor. */
  observers: FloorPlannerObserver[];
  /** planner refid → minted entity bID (from `convertPlanToTwin`). */
  refidToBid: Record<string, string>;
}

/**
 * The plan observers Go Live would actually file: a real polygon, a stream, and a target entity the
 * conversion minted.
 *
 * ⚠ **One predicate, two readers, and that is the point.** The Go Live pre-flight counts the loss
 * with it and `buildConfigurationStatement` builds the statement with it, so what an operator is
 * asked to accept and what is actually filed cannot drift apart. A statement counting what is DRAWN
 * rather than what is FILED understates the loss, and understating it is exactly the number the
 * operator is being asked to agree to.
 */
export const selectFileableObservers = ({
  observers,
  refidToBid,
}: SelectFileableObserversParams): FloorPlannerObserver[] =>
  observers.filter(
    o =>
      !o._deleted &&
      (o.points?.length ?? 0) >= 3 &&
      o.rtspEndpointId != null &&
      !!refidToBid[o.targetRefid],
  );

export interface ComputeObserverLossParams {
  /** Output of `selectFileableObservers` — what the new configuration version would hold. */
  fileable: FloorPlannerObserver[];
  /** planner refid → minted entity bID. */
  refidToBid: Record<string, string>;
  /**
   * Observers in the configuration that is currently active.
   *
   * ⚠ **Structural rather than `TwinSenseObserver[]`, so that two shapes can describe the same
   * fact.** Go Live's pre-flight now establishes what a twin is measuring by reading its
   * configuration STATEMENT rather than the observers route (`go-live-statement`, epic ledger row
   * 16), and a statement observer is not a `TwinSenseObserver`. These three fields are everything
   * this function reads; a `TwinSenseObserver` still satisfies them, so every existing caller and
   * test is unaffected.
   */
  running: RunningObserver[];
  /**
   * Live entity bIDs this activation also DELETES and lists in their own right: Launch passes the
   * removed AREAS (`launch-single-confirmation`), so a removed machine with a camera is reported here
   * as losing measurement.
   *
   * Excluded from `entitiesLosing`, because Launch's dialog already lists them as removed and
   * listing them twice would read as a second loss.
   */
  removedEntityBids: string[];
  /** Display name per entity bID, where one is known — for the named list. */
  namesByBid?: ReadonlyMap<string, string>;
  /**
   * Renumbered spaces, old full BID → new (`floor-plan-page-launch-bid-changes`, R13). A running
   * observer on `from` (or `from.N`) is the same observer the plan files on `to`, so it is compared
   * under its new BID and not counted as lost. Absent: none.
   */
  renames?: ReadonlyMap<string, string>;
}

export interface ObserverLoss {
  /** Observers the new version would hold. */
  filedCount: number;
  /** Observers measuring in the running configuration (feed + region). */
  runningCount: number;
  /** Distinct entities those running observers measure. */
  runningEntityCount: number;
  /** Entities measured now that the new version would not measure, excluding deleted ones. */
  entitiesLosing: EntityRef[];
  /**
   * Rows the read actually returned, before the measuring filter.
   *
   * Reported separately from `runningCount` because `runningMayBeTruncated` is a fact about the
   * READ and `runningCount` is a fact about the data: quoting the measuring subset while claiming
   * it is the read's maximum produces a sentence that is false on its face ("this twin reports 940
   * observers, which is the maximum a single read returns").
   */
  runningRowCount: number;
  /** The running list may have been truncated by PostgREST — see `POSTGREST_MAX_ROWS`. */
  runningMayBeTruncated: boolean;
  /**
   * Running observers that are not measuring — no stream, no region, or neither — that the new
   * version will not carry; one entry per observer, named for its entity
   * (`refiling-preserves-configuration`).
   *
   * These were created automatically from the equipment register and never set up. Nothing ever
   * measured them, so they are not in `entitiesLosing`, and filing drops them because it files only
   * observers with a region and a stream (`selectFileableObservers`). Rob ruled on 2026-09-24 that
   * dropping them is acceptable — but not silently: before this list existed a no-edit Go Live on
   * staging PUL-CON removed fourteen of them and the dialog said nothing at all.
   *
   * An entity the operator has set up an observer for in this session is not listed: its
   * placeholder is being replaced, not lost.
   */
  unconfiguredDropped: EntityRef[];
}

/**
 * What this activation would cost, per ENTITY rather than per observer.
 *
 * Per-entity is the honest unit: an entity carrying three observers on three feeds has not "lost
 * three things" when the plan carries none of them — it has stopped being measured, once. It is
 * also the unit the operator recognises, because the entity is what they see on the floor plan.
 */
export const computeObserverLoss = ({
  fileable,
  refidToBid,
  running,
  removedEntityBids,
  namesByBid,
  renames,
}: ComputeObserverLossParams): ObserverLoss => {
  const measuring = running.filter(isMeasuring);
  /** A running row's entity, under the renumbering where it has one. */
  const runningBase = (bid: string) => baseEntityBid(renames ? renumberBid(bid, renames) : bid);

  /*
   * Entities the new version WOULD measure, by base identifier.
   *
   * The `filter(Boolean)` comes BEFORE the fold, not after. `baseEntityBid` calls `.replace` on
   * its argument, so an observer whose `targetRefid` is absent from `refidToBid` would throw
   * rather than be ignored — and this module is exported for feature 04 to reuse, which is
   * exactly the caller that might hand over an unfiltered list.
   */
  const filedEntities = new Set(
    fileable
      .map(o => refidToBid[o.targetRefid])
      .filter(Boolean)
      .map(baseEntityBid),
  );

  const deleted = new Set(removedEntityBids.map(baseEntityBid));

  const runningEntities = new Set(measuring.map(o => runningBase(o.twin_entity_bid)));

  const entitiesLosing: EntityRef[] = [];
  // `Array.from`, not `for…of` over the Set directly: this app compiles to an ES5 target without
  // `downlevelIteration`, where iterating a Set is a compile error here and — in the shared
  // packages ts-jest maps into — silently iterates NOTHING.
  for (const bID of Array.from(runningEntities)) {
    if (filedEntities.has(bID)) continue;
    if (deleted.has(bID)) continue;
    // Fall back to the identifier when no name is known — an unnamed row in this list is still
    // something the operator needs to see, and the identifier is what admin shows it as.
    entitiesLosing.push({ bID, name: namesByBid?.get(bID) ?? bID });
  }

  // Stable, so the dialog's first ten do not reshuffle between the two reads either side of the
  // confirmation — the re-check compares these lists and an order change would read as a change.
  entitiesLosing.sort((a, b) => a.bID.localeCompare(b.bID));

  /*
   * Everything running that is NOT measuring — no stream, no region, or neither — so that the three
   * sets partition what runs: measuring (counted in `entitiesLosing` when lost), filed, and this.
   * A half-configured observer is dropped by filing just as a blank one is, and must not be the one
   * kind that disappears with nothing said.
   */
  const unconfiguredDropped: EntityRef[] = running
    .filter(o => !isMeasuring(o))
    .filter(o => !filedEntities.has(runningBase(o.twin_entity_bid)))
    .map(o => {
      const bID = o.twin_entity_bid.trim();
      return { bID, name: namesByBid?.get(baseEntityBid(bID)) ?? bID };
    })
    .sort((a, b) => a.bID.localeCompare(b.bID));

  return {
    filedCount: fileable.length,
    unconfiguredDropped,
    runningCount: measuring.length,
    runningEntityCount: runningEntities.size,
    entitiesLosing,
    runningRowCount: running.length,
    runningMayBeTruncated: running.length >= POSTGREST_MAX_ROWS,
  };
};
